import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { PasswordInput } from '@/components/ui/app/password-input';
import { EmailInput } from '@/components/ui/app/text-input';
import { BackButton } from '@/components/ui/app/back-button';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function CreatePasswordForm({ onBack, onSwitchToLogin, token: propToken }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Token can come from props or URL params
    const token = propToken || searchParams.get('token') || '';
    const emailFromUrl = searchParams.get('email') || '';

    const [email, setEmail] = useState(emailFromUrl);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        if (!email) {
            setErrorMessage('Veuillez saisir votre adresse email.');
            return;
        }

        if (!password || !confirmPassword) {
            setErrorMessage('Veuillez remplir tous les champs.');
            return;
        }

        if (password !== confirmPassword) {
            setErrorMessage('Les mots de passe ne correspondent pas.');
            return;
        }

        if (password.length < 8) {
            setErrorMessage('Le mot de passe doit contenir au moins 8 caractères.');
            return;
        }

        if (!token) {
            setErrorMessage('Lien de création de mot de passe invalide ou expiré.');
            return;
        }

        setIsSubmitting(true);
        try {
            await horodatyApi.createPassword({
                token,
                email,
                password,
                password_confirmation: confirmPassword,
            });
            setIsSuccess(true);
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                t('welcome:impossibleDeCreerLeMotDePasseLe');
            setErrorMessage(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="relative flex flex-col min-h-full">
                <div className="shrink-0">
                    <BackButton onClick={onBack} />
                </div>

                <div className="flex flex-col items-center justify-center flex-1 max-w-md mx-auto w-full text-center py-8">
                    <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle className="w-8 h-8 text-success" />
                    </div>

                    <h2 className="text-4xl font-black mb-4 text-center">{t('welcome:motDePasseCree')}</h2>

                    <p className="text-muted-foreground mb-8">
                        {t('welcome:votreMotDePasseAEteCreeAvec')}
                    </p>

                    <Button
                        variant="solid"
                        color="primary"
                        size="cta"
                        className="w-full max-w-xs"
                        onClick={() => {
                            if (onSwitchToLogin) {
                                onSwitchToLogin();
                            } else {
                                navigate('/');
                            }
                        }}
                    >
                        {t('welcome:seConnecter')}
                    </Button>
                </div>
            </div>
        );
    }

    if (!token) {
        return (
            <div className="relative flex flex-col min-h-full">
                <div className="shrink-0">
                    <BackButton onClick={onBack} />
                </div>

                <div className="flex flex-col items-center justify-center flex-1 max-w-md mx-auto w-full text-center py-8">
                    <h2 className="text-4xl font-black mb-4 text-center">{t('welcome:lienInvalide')}</h2>

                    <p className="text-muted-foreground mb-8">
                        {t('welcome:leLienDeCreationDeMotDePasse')}
                    </p>

                    <Button variant="link" onClick={onBack} className="text-brand font-medium">
                        {t('welcome:retourALAccueil')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="relative flex flex-col min-h-full">
            <div className="shrink-0">
                <BackButton onClick={onBack} />
            </div>

            <div className="flex flex-col items-center justify-center flex-1 max-w-md mx-auto w-full py-8">
                <h2 className="text-4xl font-black mb-4 text-center">{t('welcome:creezVotreMotDePasse')}</h2>

                <p className="text-muted-foreground text-center mb-8">
                    {t('welcome:bienvenueCreezVotreMotDePassePourAcceder')}
                </p>

                <form className="w-full space-y-4" onSubmit={handleSubmit}>
                    <EmailInput
                        placeholder={t('welcome:email')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />

                    <div>
                        <PasswordInput
                            placeholder={t('welcome:nouveauMotDePasse')}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <p className="text-xs text-muted-foreground mt-2 px-2">
                            {t('welcome:auMoins8CaracteresIncluantUneMajusculeUne')}
                        </p>
                    </div>

                    <PasswordInput
                        placeholder={t('welcome:confirmerLeMotDePasse')}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                    />

                    {errorMessage && <div className="text-sm font-medium text-error px-2">{errorMessage}</div>}

                    <SubmitButton
                        color="primary"
                        size="cta"
                        className="w-full mt-6"
                        isSubmitting={isSubmitting}
                        loadingText={t('welcome:creation')}
                    >
                        {t('welcome:creerMonMotDePasse')}
                    </SubmitButton>
                </form>

                <p className="mt-6 text-muted-foreground">
                    {t('welcome:vousAvezDejaUnMotDePasse')}{' '}
                    <Button variant="link" onClick={onSwitchToLogin} className="text-brand font-medium p-0 h-auto">
                        {t('welcome:seConnecter')}
                    </Button>
                </p>
            </div>
        </div>
    );
}
